import { useState } from 'react';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const baseUrl = 'https://hanmandq.com';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩漫大全',
  url: baseUrl,
  description: '韩漫大全免费平台是韩国漫画在线阅读网标杆，每日更新最新章节，提供海量韩漫资源。',
  foundingDate: '2020',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@hanmandq.com',
    availableLanguage: ['Chinese'],
  },
  sameAs: [
    'https://weibo.com/hanmandq',
    'https://weixin.qq.com/hanmandq',
  ],
};

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于韩漫大全 - 韩国漫画在线阅读平台',
  description: '了解韩漫大全的品牌故事、平台优势与联系方式。韩漫大全致力于为全球韩漫爱好者提供优质的漫画阅读体验。',
  url: `${baseUrl}/about`,
  isPartOf: { '@id': `${baseUrl}/#website` },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, item: { '@id': baseUrl, name: '首页' } },
    { '@type': 'ListItem', position: 2, item: { '@id': `${baseUrl}/about`, name: '关于我们' } },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '韩漫大全如何保证更新速度？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩漫大全拥有专业的运营团队，与多家韩国漫画平台保持密切合作，确保每天第一时间同步更新最新章节。我们的编辑团队24小时轮值，为读者提供最及时的更新服务。',
      },
    },
    {
      '@type': 'Question',
      name: '韩漫大全的漫画资源覆盖面有多广？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩漫大全目前收录超过10000部漫画作品，涵盖恋爱、奇幻、动作、悬疑、搞笑、剧情、耽美、百合等全部分类。无论是热门大作还是小众精品，都可以在韩漫大全平台找到。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在韩漫大全反馈问题？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过本页面的联系表单、发送邮件至contact@hanmandq.com，或通过微信公众号与我们取得联系。我们的客服团队会在24小时内回复您的反馈。',
      },
    },
  ],
};

const accolades = [
  { year: '2025', title: '最佳韩漫阅读平台', desc: '荣获年度最佳韩国漫画在线阅读平台奖项' },
  { year: '2024', title: '用户最喜爱漫画应用', desc: '千万用户票选最受欢迎漫画阅读平台' },
  { year: '2023', title: '最具影响力漫画品牌', desc: '被评为韩国漫画领域最具影响力品牌之一' },
  { year: '2022', title: '最快增长平台奖', desc: '年度用户增长率最高的漫画阅读平台' },
];

export default function AboutPage() {
  const [formState, setFormState] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setStatus('success');
      setStatusMessage('感谢您的留言，我们会尽快回复！');
      setFormState({ name: '', email: '', subject: '', message: '' });
      return;
    }

    setStatus('loading');
    setStatusMessage('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9oti5qqs38sj6jc7fk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMessage('感谢您的留言！我们会在24小时内回复您。');
        setFormState({ name: '', email: '', subject: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setStatus('error');
          setStatusMessage('提交失败，请稍后再试。');
        } else {
          setStatus('error');
          setStatusMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setStatus('error');
      setStatusMessage('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <>
      <SEOMeta
        title="关于韩漫大全 - 韩国漫画在线阅读平台 | 品牌介绍与联系方式"
        description="了解韩漫大全的品牌故事与平台优势。韩漫大全免费平台是韩国漫画在线阅读网标杆，每日更新最新章节，提供海量韩漫资源。查看平台荣誉、用户评价与联系方式。"
        keywords="韩漫大全,关于韩漫大全,韩国漫画,在线阅读网,韩漫平台介绍,联系我们"
        canonicalUrl={`${baseUrl}/about`}
        ogImage="https://readdy.ai/api/search-image?query=Korean%20manhwa%20reading%20platform%20about%20us%20banner%2C%20warm%20and%20professional%20illustration%20style%2C%20books%20and%20digital%20reading%20theme%2C%20clean%20corporate%20banner%2C%20inviting%20atmosphere&width=1200&height=630&seq=hanman-about-og&orientation=landscape"
        schemaData={[organizationSchema, aboutSchema, breadcrumbSchema, faqSchema]}
      />

      <Navbar />

      <main className="pt-20 pb-16">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-8 text-center max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-4">
              关于韩漫大全
            </h1>
            <p className="text-foreground-600 leading-relaxed">
              韩漫大全（hanmandq.com）成立于2020年，是韩国漫画在线阅读领域的标杆平台。我们致力于为全球韩漫爱好者提供最全面、最及时、最优质的漫画阅读服务。
            </p>
          </section>

          <section className="mt-16">
            <div className="flex flex-col lg:flex-row gap-12 items-center">
              <div className="lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20and%20modern%20digital%20reading%20platform%20illustration%2C%20Korean%20manhwa%20style%20artwork%20on%20digital%20devices%2C%20cozy%20reading%20atmosphere%20with%20warm%20lighting%2C%20clean%20and%20inviting%20composition%2C%20professional%20yet%20friendly%20aesthetic&width=800&height=600&seq=hanman-about-brand&orientation=landscape"
                  alt="韩漫大全品牌形象"
                  className="w-full rounded-xl object-cover"
                />
              </div>
              <div className="lg:w-1/2">
                <h2 className="text-2xl font-heading text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  韩漫大全诞生于一群韩漫爱好者的共同热爱。我们深知漫画读者对内容质量、更新速度和阅读体验的高要求，因此从创立之初就将"做最好的韩漫阅读平台"作为核心使命。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  经过多年的发展，韩漫大全已从一个小型漫画分享站成长为拥有超过500万活跃用户的综合性韩漫阅读平台。我们与多家韩国知名漫画平台建立了合作关系，确保内容的正版授权与品质保障。
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  我们相信，优秀的漫画作品值得被更多人看到。韩漫大全将继续坚持免费阅读的初心，为每一位漫画爱好者提供无障碍的阅读体验。
                </p>
              </div>
            </div>
          </section>

          <section className="mt-20">
            <h2 className="text-2xl font-heading text-foreground-900 text-center mb-10">平台优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-book-open-line', title: '海量资源', desc: '收录超过10000部韩国漫画，涵盖全部分类，热门冷门一网打尽' },
                { icon: 'ri-timer-flash-line', title: '每日更新', desc: '专业团队24小时轮值，确保第一时间同步最新章节更新' },
                { icon: 'ri-smartphone-line', title: '多端阅读', desc: '支持电脑、平板、手机等多种设备，随时随地畅享阅读' },
                { icon: 'ri-shield-check-line', title: '品质保障', desc: '严格的内容审核机制，确保每一部漫画都符合高品质标准' },
              ].map((item, index) => (
                <div key={index} className="bg-background-50 rounded-xl border border-background-200/70 p-6 text-center hover:border-primary-200 transition-colors">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-heading text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-20">
            <h2 className="text-2xl font-heading text-foreground-900 text-center mb-10">平台荣誉</h2>
            <div className="relative">
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-background-200/70"></div>
              <div className="space-y-8">
                {accolades.map((item, index) => (
                  <div key={index} className={`flex flex-col lg:flex-row items-start gap-6 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                    <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:text-left lg:pl-12'}`}>
                      <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium whitespace-nowrap">
                        {item.year}
                      </span>
                      <h3 className="text-lg font-heading text-foreground-900 mt-3">{item.title}</h3>
                      <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                    </div>
                    <div className="hidden lg:flex items-center justify-center shrink-0">
                      <div className="w-4 h-4 rounded-full bg-accent-500 border-4 border-background-50 relative z-10"></div>
                    </div>
                    <div className="lg:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-20">
            <h2 className="text-2xl font-heading text-foreground-900 text-center mb-4">用户口碑</h2>
            <p className="text-center text-foreground-500 mb-10">来自真实用户的评价</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: '小橘子', text: '韩漫大全真的是我用过最好用的韩漫平台！更新超级快，画质也很清晰，而且完全免费，太良心了！', rating: 5 },
                { name: '漫画迷阿杰', text: '追了三年韩漫了，韩漫大全一直是我的首选。分类很全，新番老番都有，推荐系统也很准，总能发现好看的漫画。', rating: 5 },
                { name: '星星点灯', text: '界面简洁好看，没有乱七八糟的广告。收藏功能很方便，更新了第一时间就能看到。希望韩漫大全越做越好！', rating: 5 },
                { name: '二次元少女', text: '每天必刷的网站！最喜欢恋爱类的韩漫，韩漫大全的分类做得特别好，找漫画超级方便。', rating: 5 },
                { name: '追漫人小林', text: '从大学开始用韩漫大全到现在工作，见证了这个平台越来越好。更新的速度和质量一直很稳定，值得信赖。', rating: 4 },
                { name: '花卷少女', text: '强烈推荐韩漫大全！资源丰富不说，阅读体验也很棒。支持收藏和评论，感觉找到了组织。', rating: 5 },
              ].map((review, index) => (
                <div key={index} className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }, (_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.text}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-600">{review.name[0]}</span>
                    </div>
                    <span className="text-sm text-foreground-700 font-medium">{review.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-20 max-w-2xl mx-auto">
            <h2 className="text-2xl font-heading text-foreground-900 text-center mb-4">联系我们</h2>
            <p className="text-center text-foreground-500 mb-8">
              有任何问题或建议？欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</h3>
                <a href="mailto:contact@hanmandq.com" className="text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap">
                  contact@hanmandq.com
                </a>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 mb-1">微信公众号</h3>
                <span className="text-xs text-foreground-500">搜索"韩漫大全"关注</span>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-weibo-line text-lg"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 mb-1">官方微博</h3>
                <span className="text-xs text-foreground-500">@韩漫大全官方</span>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
              <h3 className="text-lg font-heading text-foreground-900 mb-6">发送消息</h3>
              <form className="space-y-5" data-readdy-form="true" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-foreground-600 mb-1.5" htmlFor="af-name">您的姓名</label>
                    <input
                      id="af-name"
                      type="text"
                      name="name"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                      value={formState.name}
                      onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-600 mb-1.5" htmlFor="af-email">电子邮箱</label>
                    <input
                      id="af-email"
                      type="email"
                      name="email"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                      value={formState.email}
                      onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-foreground-600 mb-1.5" htmlFor="af-subject">主题</label>
                  <input
                    id="af-subject"
                    type="text"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入消息主题"
                    value={formState.subject}
                    onChange={(e) => setFormState({ ...formState, subject: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-foreground-600 mb-1.5" htmlFor="af-message">留言内容</label>
                  <textarea
                    id="af-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言（500字以内）"
                    value={formState.message}
                    onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                    required
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">{formState.message.length}/500</p>
                </div>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium text-sm transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {status === 'loading' ? '发送中...' : '发送消息'}
                </button>
                {statusMessage && (
                  <p className={`text-center text-sm ${status === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                    {statusMessage}
                  </p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}